
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Report, AppSettings } from '../types';
import { generatePDF } from '../services/pdfService';
import { ladeFreimeldungQrFuerBericht } from '../services/freimeldungService';
import { Button } from './Button';
import { Download, Loader2, FileText, AlertTriangle, Share2 } from 'lucide-react';
import { istNativeApp } from '../services/nativePlatform';
import { ausgabeFehlerMelden, dateiAusgeben } from '../services/dateiAusgabe';
import { registriereZurueck } from '../services/appUmfang';
import { useIsMobile } from '../hooks/useIsMobile';
import { NativePdfSeiten } from './NativePdfSeiten';
import { vorschauStandFuer, type VorschauStand } from './pdfVorschauStand';

/** Seitenansicht (pdf.js): so viele Seiten sofort, je „Weitere Seiten" so viele dazu — Speicher der WebView. */
const VORSCHAU_SEITEN = 10;

interface Props {
  report: Report;
  settings?: AppSettings;
  onClose: () => void;
  onUpdate?: (report: Report) => void;
  /**
   * Schreibgeschützt geöffnetes Projekt: die Vorschau darf nichts schreiben.
   * Wird weiter an generatePDF durchgereicht (`skipMigration`). Seit
   * 30.09.2026 lädt der Generator nur noch die Kopfliste des Archivs, die nie
   * zurückschreibt — der Schalter ist dort derzeit ohne Wirkung, bleibt aber
   * als Schutz, falls wieder volle Schnappschüsse gelesen werden.
   */
  isReadOnly?: boolean;
  /**
   * Ist die Online-Freimeldung für das Projekt FREIGESCHALTET
   * (`metadata.freimeldung.aktiv`, Vorgabe an)?
   *
   * DER AUFRUFER RECHNET DAS AUS, und zwar aus den AKTUELLEN
   * Projekt-Metadaten — nicht die Vorschau aus `report.metadata`. `report` ist
   * hier oft ein ARCHIV-SCHNAPPSCHUSS (`full_report_json` eines längst
   * versendeten Berichts). In dem steht der Stand von damals eingefroren:
   * Nimmt der Betreiber den Haken zurück, sind alle gedruckten QR-Codes des
   * Projekts sofort tot (`report_freimeldung_public` → null), der Snapshot
   * trüge aber weiter `aktiv: true` — die Vorschau druckte dann einen Kasten
   * mit einem Code, der den Empfänger auf „Dieser Link ist nicht mehr gültig"
   * führt. Genau das soll nicht passieren.
   *
   * FEHLT die Angabe, gilt AUS und es wird kein QR nachgeschlagen: wer den
   * aktuellen Stand nicht kennt (z. B. eine reine Archivansicht ohne
   * geladenes Projekt), darf nicht raten.
   */
  freimeldungAktiv?: boolean;
  /**
   * Der eingeloggte Mensch, der den Bericht gerade schreibt. Weicht er vom
   * Hauptverantwortlichen ab, weist ihn das Protokoll als Durchfuehrenden aus.
   * Nur fuer die Live-Vorschau; Archiv-Snapshots tragen den Namen selbst.
   */
  conductedByName?: string;
}

export const PdfPreviewModal: React.FC<Props> = ({ report: reportProp, settings: settingsProp, onClose, isReadOnly = false, freimeldungAktiv = false, conductedByName }) => {
  const [stand, setStand] = useState<VorschauStand>(() => vorschauStandFuer(null, reportProp, settingsProp));
  const naechsterStand = vorschauStandFuer(stand, reportProp, settingsProp);
  if (naechsterStand !== stand) setStand(naechsterStand);
  const { report, settings } = stand;
  // Laufnummer der Erzeugung: nur der jüngste Lauf darf sein Ergebnis zeigen.
  const laufRef = useRef(0);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Das einmal generierte jsPDF-Dokument wird zwischengespeichert und beim
  // Download wiederverwendet → keine teure Zweit-Generierung (Bildoptimierung).
  const docRef = useRef<any>(null);
  // Aktive Blob-URL separat halten, damit sie NICHT bei jedem Prop-Wechsel
  // vorzeitig freigegeben wird (das würde die im iframe/object aktive URL töten
  // → leere Vorschau). Revoke nur beim Ersetzen und beim Unmount.
  const objectUrlRef = useRef<string | null>(null);
  // Android-App (Capacitor-Hülle): kein PDF-Betrachter in der WebView und
  // keine Blob-Downloads — Vorschau über pdf.js, Ausgabe über den Teilen-Dialog.
  // Im Browser bleibt alles wie gehabt.
  const nativ = istNativeApp();
  const [teilenLaeuft, setTeilenLaeuft] = useState(false);
  // Handy-Browser: dieselbe Seitenansicht wie in der App — ein <object> bleibt
  // dort leer (Android) bzw. zeigt nur Seite 1 (iOS). Desktop: unverändert.
  const isMobile = useIsMobile();
  const seitenAnsicht = nativ || isMobile;

  // Android-Zurück-Taste (nur in der App wirksam): schließt wie „Schließen" —
  // eine reine Ansicht, es geht nichts verloren.
  const flaecheRef = useRef<HTMLDivElement>(null);
  const schliessenRef = useRef(onClose);
  useEffect(() => { schliessenRef.current = onClose; });
  useEffect(() => registriereZurueck(() => schliessenRef.current(), () => flaecheRef.current), []);

  // Rein LESEND: die Vorschau und der Download legen nie einen Freimelde-Link
  // an. Vor dem ersten Versand gibt es keinen, dann bleibt der Kasten weg;
  // nach dem Versand traegt das Vorschau-PDF denselben QR wie die Mail.
  //
  // IST DAS PROJEKT NICHT FREIGESCHALTET, wird gar nicht nachgeschlagen
  // (`metadata.freimeldung.aktiv`, Vorgabe an). Die Vorschau muss denselben
  // Kasten zeigen wie das versendete PDF — und dort steht bei einem
  // stillgelegten Projekt keiner. Der Dienst prueft das nicht selbst: er
  // bekommt nur Kennungen, nicht die Metadaten.
  //
  // GEFRAGT WIRD NUR DAS PROP, NIE `report.metadata`: `report` ist beim
  // Vorschauen eines Archiveintrags der SCHNAPPSCHUSS von damals und traegt
  // den Schalterstand des Versandtags. Nach dem Abschalten druckte die
  // Vorschau sonst weiter einen QR-Code, den die Datenbank laengst nicht mehr
  // beantwortet ("Dieser Link ist nicht mehr gültig"). Der aktuelle Stand
  // steht nur beim Aufrufer, der das Projekt geladen hat.
  const ladeQr = useCallback(async () => {
    if (!freimeldungAktiv) return null;
    const projektId = (report.metadata.globalData?.projectId || '').trim();
    const nummer = report.metadata.inspectionNumber;
    if (!projektId || !nummer) return null;
    return ladeFreimeldungQrFuerBericht(projektId, nummer, report.metadata.sectionId ?? null, window.location.origin, window.location.pathname);
  }, [report, freimeldungAktiv]);

  useEffect(() => {
    let cancelled = false;
    // Ein überholter Lauf rechnet nicht weiter (Signal an generatePDF) und
    // kann ein neueres Ergebnis nie überschreiben (Laufnummer + cancelled).
    const lauf = ++laufRef.current;
    const abbruch = new AbortController();
    const veraltet = () => cancelled || lauf !== laufRef.current;

    const createPdf = async () => {
      try {
        setLoading(true);
        setError(null);
        const freimeldungQr = await ladeQr();
        if (veraltet()) return;
        const doc = await generatePDF(report, settings, undefined, undefined, { skipMigration: isReadOnly, conductedByName, freimeldungQr, signal: abbruch.signal });
        if (veraltet()) return;
        docRef.current = doc;
        // Blob-URL statt datauristring: große Daten-URIs werden von iframes
        // teils gar nicht gerendert (leere Vorschau) und sind ~33% größer.
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        const url = URL.createObjectURL(doc.output('blob'));
        objectUrlRef.current = url;
        setPdfUrl(url);
      } catch (e) {
        if (veraltet()) return;
        console.error("PDF Gen Error", e);
        docRef.current = null;
        setError(e instanceof Error ? e.message : 'Unbekannter Fehler bei der PDF-Erzeugung');
      } finally {
        if (!veraltet()) setLoading(false);
      }
    };
    createPdf();

    return () => { cancelled = true; abbruch.abort(); };
  }, [report, settings, isReadOnly, conductedByName, ladeQr]);

  // Letzte Blob-URL beim Schließen freigeben (kein Memory-Leak).
  useEffect(() => () => { if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); }, []);

  const handleDownload = async () => {
      // Reiner Download ohne Side-Effects auf den Status oder die Abrechnung.
      // Bereits generiertes Dokument wiederverwenden, sonst (Fallback) erzeugen.
      const doc = docRef.current || await generatePDF(report, settings, undefined, undefined, { skipMigration: isReadOnly, conductedByName, freimeldungQr: await ladeQr() });

      // FILENAME LOGIC
      const defaultTemplate = "{datum}_{projekt}_Bericht_Nr.{nr}";
      let tmpl = settings?.filenameTemplate || defaultTemplate;
      if (!tmpl.trim()) tmpl = defaultTemplate;

      const meta = report.metadata;
      const activeSection = meta.globalData?.sections?.find(s => s.id === meta.sectionId);

      const replacements: Record<string, string> = {
        '{projekt}': (meta.projectName || 'Projekt').replace(/[^a-z0-9äöüß\s-]/gi, '_'),
        '{nr}': (meta.inspectionNumber || 0).toString() + (activeSection?.projectNumberSuffix || ''),
        '{datum}': (meta.date ? new Date(meta.date) : new Date()).toISOString().split('T')[0].replace(/-/g, '_'),
        '{ort}': (meta.projectAddress || '').split(',')[0].replace(/[^a-z0-9äöüß\s-]/gi, '_')
      };

      Object.entries(replacements).forEach(([key, val]) => {
        tmpl = tmpl.replace(new RegExp(key, 'g'), val);
      });

      const filename = tmpl.endsWith('.pdf') ? tmpl : `${tmpl}.pdf`;
      if (nativ) {
          // Abgewartet und der Knopf solange gesperrt: ein zweites Tippen,
          // während der Teilen-Dialog noch aufgeht, liefe ins „Teilen läuft schon".
          setTeilenLaeuft(true);
          try {
              await dateiAusgeben(doc.output('blob'), filename);
          } catch (e) {
              ausgabeFehlerMelden(e);
          } finally {
              setTeilenLaeuft(false);
          }
      } else {
          doc.save(filename);
      }

      // WICHTIG: updateProjectExportStatus und onUpdate werden hier bewusst NICHT aufgerufen,
      // damit der Bericht beim Download aus der reinen Vorschau NICHT als erledigt markiert wird.
  };

  return (
    <div ref={flaecheRef} className={`fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center animate-fade-in ${isMobile ? '' : 'p-4'}`}>
      {/* Am Handy Vollbild: 90vh mit Rand kostet dort ein Drittel der Seite. */}
      <div className={`bg-white shadow-2xl w-full flex flex-col overflow-hidden animate-scale-in ${isMobile ? 'h-full' : 'rounded-2xl max-w-5xl h-[90vh]'}`}>
        <div className="bg-slate-900 text-white p-4 flex flex-col gap-4 shrink-0" style={isMobile ? { paddingTop: 'max(1rem, env(safe-area-inset-top))' } : undefined}>
          <div className="flex justify-between items-center gap-3">
              <div className="flex items-center gap-3 min-w-0">
                 <div className="bg-white/10 p-2 rounded-lg"><FileText className="w-5 h-5"/></div>
                 <div className="min-w-0">
                     <h3 className="font-bold text-lg">Berichtsvorschau</h3>
                     <p className="text-xs text-slate-400">Prüfen Sie das Layout vor dem Export</p>
                 </div>
              </div>
              <Button variant="ghost" onClick={onClose} className={`text-white hover:bg-white/10 ${isMobile ? 'min-h-[44px] shrink-0' : ''}`}>Schließen</Button>
          </div>

          <div className="flex justify-end pt-2 border-t border-slate-700/50">
             <Button
                data-tutorial="pdf.herunterladen"
                variant="primary"
                onClick={handleDownload}
                disabled={loading || !!error || teilenLaeuft}
                className={`bg-blue-600 hover:bg-blue-700 ${isMobile ? 'min-h-[44px]' : ''}`}
                icon={nativ ? (teilenLaeuft ? <Loader2 className="w-4 h-4 animate-spin"/> : <Share2 className="w-4 h-4"/>) : <Download className="w-4 h-4"/>}
             >
                {nativ ? 'PDF teilen / speichern' : 'PDF Herunterladen'}
             </Button>
          </div>
        </div>

        <div className="flex-1 bg-slate-100 relative overflow-hidden flex items-center justify-center">
            {loading ? (
                <div className="flex flex-col items-center text-slate-500">
                    <Loader2 className="w-10 h-10 animate-spin mb-4 text-blue-600"/>
                    <p className="font-bold">PDF wird generiert...</p>
                    <p className="text-xs">Bilder werden optimiert</p>
                </div>
            ) : error ? (
                <div className="flex flex-col items-center text-center text-slate-600 max-w-md px-6">
                    <AlertTriangle className="w-10 h-10 mb-4 text-amber-500"/>
                    <p className="font-bold mb-1">Vorschau konnte nicht erstellt werden</p>
                    <p className="text-xs text-slate-500 break-words">{error}</p>
                </div>
            ) : pdfUrl && seitenAnsicht ? (
                // Erst 10 Seiten als Bilder (Speicher der WebView), weitere auf
                // Tipp; darunter der Kürzungshinweis samt Ausgabe-Knopf fürs volle PDF.
                <NativePdfSeiten
                    key={pdfUrl}
                    pdfUrl={pdfUrl}
                    maxSeiten={VORSCHAU_SEITEN}
                    nachladenSchritt={VORSCHAU_SEITEN}
                    onTeilen={handleDownload}
                    teilenLaeuft={teilenLaeuft}
                    teilenText={nativ ? undefined : 'PDF Herunterladen'}
                />
            ) : pdfUrl ? (
                <object data={pdfUrl} type="application/pdf" className="w-full h-full" aria-label="PDF Preview">
                    {/* Fallback, wenn der Browser PDFs nicht inline rendert (häufig auf Mobil/PWA). */}
                    <div className="flex flex-col items-center text-center text-slate-600 px-6 h-full justify-center">
                        <FileText className="w-10 h-10 mb-4 text-slate-400" />
                        <p className="font-bold mb-1">Inline-Vorschau nicht verfügbar</p>
                        <p className="text-xs text-slate-500 mb-4 max-w-sm">
                            Dieser Browser kann das PDF nicht direkt einbetten (typisch auf Mobilgeräten). Öffnen Sie es in einem neuen Tab oder laden Sie es herunter.
                        </p>
                        <a
                            href={pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-4 py-2 rounded-lg"
                        >
                            PDF in neuem Tab öffnen
                        </a>
                    </div>
                </object>
            ) : null}
        </div>
      </div>
    </div>
  );
};
